import { useQuery } from "convex/react";
import { api } from "../../api";
import { useCallback } from "react";
import { type NsfwPresetLevel, type AccuracyLevel, DEFAULT_BLUEJAY_CONFIG } from "@/config/nsfwRulePresets";
import { toConvexId } from "@/types/convex";

export interface BlueJayRulesData {
  categories: string[];
  allowlist: string[];
  blocklist: string[];
  config: {
    nsfwLevel: NsfwPresetLevel;
    blockVideos: boolean;
    human: boolean;
    accuracy: AccuracyLevel;
  };
}

export type BlueJayRulesResult = BlueJayRulesData | false | null;

export interface RawPolicyData {
  serial: string;
  source: string;
  policyUrl: string;
  rawResponse: any;
  parsedResult: BlueJayRulesResult;
  parseNotes: string[];
}

export interface UseBlueJayRulesReadResult {
  blueJayRules: BlueJayRulesResult;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  rawPolicy: RawPolicyData | null;
  // Additional source data for categories (for UI display)
  categorySources: Record<string, "device" | "level" | false> | null;
}

export function useBlueJayRulesRead(deviceId: string): UseBlueJayRulesReadResult {
  // Get device details to access the deviceConfig ID
  const deviceData = useQuery(
    api.devices.get.details, 
    deviceId ? { device: toConvexId.device(deviceId) } : "skip"
  );
  
  const deviceConfig = deviceData?.device?.deviceConfig;

  // Fetch policies using the new API
  const policiesData = useQuery(
    api.devices.policies.get,
    deviceConfig ? { deviceConfig } : "skip"
  );

  // Parse the blueJayRules from the response
  const parseBlueJayRules = useCallback((): BlueJayRulesResult => {
    if (!policiesData) return null;
    
    // Use unknown to handle both object and false cases at runtime
    const blueJayRulesRaw = policiesData.blueJayRules as unknown;

    // Browser disabled:
    // - Some backends return literal false
    // - Others omit the field entirely (undefined)
    if (blueJayRulesRaw === false || blueJayRulesRaw === undefined || blueJayRulesRaw === null) {
      return false;
    }
    
    // Not an object - treat as unconfigured
    if (typeof blueJayRulesRaw !== 'object') {
      return null;
    }

    // Cast to record for property access
    const rulesObj = blueJayRulesRaw as Record<string, any>;

    // Check for browser disabled flag (fallback pattern)
    if ('disabled' in rulesObj && rulesObj.disabled === true) {
      return false;
    }

    // Helper to merge from top-level Record and nested array
    const getBlockedCategories = (): string[] => {
      const blocked = new Set<string>();
      
      // Source 1: Top-level policiesData.categories Record
      const topLevelCategories = policiesData?.categories as Record<string, "device" | "level" | false> | undefined;
      if (topLevelCategories && typeof topLevelCategories === 'object') {
        Object.entries(topLevelCategories).forEach(([catName, source]) => {
          if (source === 'device' || source === 'level') {
            blocked.add(catName);
          }
        });
      }
      
      // Source 2: Nested blueJayRules.categories array (backwards compatibility)
      if (Array.isArray(rulesObj.categories)) {
        rulesObj.categories.forEach((cat: string) => blocked.add(cat));
      }
      
      return Array.from(blocked);
    };

    const getMergedAllowlist = (): string[] => {
      const merged = new Set<string>();
      
      // Source 1: Top-level allowlist Record
      const topLevelAllowlist = policiesData?.allowlist as Record<string, "device" | "level" | false> | undefined;
      if (topLevelAllowlist && typeof topLevelAllowlist === 'object') {
        Object.entries(topLevelAllowlist).forEach(([url, source]) => {
          if (source === 'device' || source === 'level') {
            merged.add(url);
          }
        });
      }
      
      // Source 2: Nested blueJayRules.allowlist array
      if (Array.isArray(rulesObj.allowlist)) {
        rulesObj.allowlist.forEach((url: string) => merged.add(url));
      }
      
      return Array.from(merged);
    };

    const getMergedBlocklist = (): string[] => {
      const merged = new Set<string>();
      
      // Source 1: Top-level blocklist Record
      const topLevelBlocklist = policiesData?.blocklist as Record<string, "device" | "level" | false> | undefined;
      if (topLevelBlocklist && typeof topLevelBlocklist === 'object') {
        Object.entries(topLevelBlocklist).forEach(([url, source]) => {
          if (source === 'device' || source === 'level') {
            merged.add(url);
          }
        });
      }
      
      // Source 2: Nested blueJayRules.blocklist array
      if (Array.isArray(rulesObj.blocklist)) {
        rulesObj.blocklist.forEach((url: string) => merged.add(url));
      }
      
      return Array.from(merged);
    };

    // Parse the blueJayRules object with merged data
    const parsedRules: BlueJayRulesData = {
      categories: getBlockedCategories(),
      allowlist: getMergedAllowlist(),
      blocklist: getMergedBlocklist(),
      config: {
        nsfwLevel: rulesObj.config?.nsfwLevel ?? DEFAULT_BLUEJAY_CONFIG.nsfwLevel,
        blockVideos: rulesObj.config?.blockVideos ?? false,
        human: rulesObj.config?.human ?? DEFAULT_BLUEJAY_CONFIG.human,
        accuracy: rulesObj.config?.accuracy ?? DEFAULT_BLUEJAY_CONFIG.accuracy,
      },
    };

    console.log("[useBlueJayRulesRead] Top-level categories:", policiesData?.categories);
    console.log("[useBlueJayRulesRead] Nested blueJayRules:", rulesObj);
    console.log("[useBlueJayRulesRead] Merged result:", parsedRules);
    return parsedRules;
  }, [policiesData]);

  const blueJayRules = parseBlueJayRules();

  // Extract category sources for UI display (from top-level categories field)
  const categorySources = policiesData?.categories ?? null;

  // Build raw policy data for debugging
  const counts = (() => {
    const rules: BlueJayRulesResult = blueJayRules;
    if (rules === null || rules === false) {
      return { categories: 0, allowlist: 0, blocklist: 0 };
    }
    return {
      categories: rules.categories.length,
      allowlist: rules.allowlist.length,
      blocklist: rules.blocklist.length,
    };
  })();
  
  const rawPolicy: RawPolicyData | null = deviceData?.device?.serial ? {
    serial: deviceData.device.serial,
    source: "Convex API (devices.policies.get)",
    policyUrl: `api.devices.policies.get({ deviceConfig: ${deviceConfig} })`,
    rawResponse: policiesData,
    parsedResult: blueJayRules,
    parseNotes: [
      `Fetched via Convex query`,
      `Categories: ${counts.categories} blocked`,
      `Allowlist: ${counts.allowlist} URLs`,
      `Blocklist: ${counts.blocklist} URLs`,
    ],
  } : null;

  // Refetch is now a no-op since Convex queries are reactive
  const refetch = useCallback(() => {
    console.log("[useBlueJayRulesRead] Refetch called - Convex queries are reactive, no manual refetch needed");
  }, []);

  const isLoading = deviceId ? (deviceData === undefined || policiesData === undefined) : false;

  return {
    blueJayRules,
    isLoading,
    error: null, // Convex handles errors differently
    refetch,
    rawPolicy,
    categorySources,
  };
}
